import { Injectable, signal } from '@angular/core';

export type ProjectChangeType = 'created' | 'updated' | 'deleted';

export interface ProjectChange {
  type: ProjectChangeType;
  projectId: number;
}

/**
 * Signal-based notifier for project create/update/delete events.
 *
 * Replaces the previous `window.dispatchEvent(new CustomEvent('project-created' | …))`
 * bus, which bypassed Angular DI and change detection and relied on stringly-typed
 * event names. Producers (dialogs, sidebar) call `notifyCreated/Updated/Deleted`;
 * consumers either read `lastChange()` or fold `changeTick()` into a `resource()`'s
 * params so the resource refetches whenever a project changes.
 */
@Injectable({ providedIn: 'root' })
export class ProjectEventsService {
  readonly #changeTick = signal(0);
  readonly #lastChange = signal<ProjectChange | null>(null);

  /**
   * Monotonically increases on every project change. Fold this into a `resource()`
   * params computation to trigger an automatic refetch after a mutation, e.g.:
   * `params: computed(() => ({ …, tick: events.changeTick() }))`.
   */
  readonly changeTick = this.#changeTick.asReadonly();

  /** The most recent project change, or `null` if none has occurred this session. */
  readonly lastChange = this.#lastChange.asReadonly();

  notifyCreated(projectId: number): void {
    this.#emit('created', projectId);
  }

  notifyUpdated(projectId: number): void {
    this.#emit('updated', projectId);
  }

  notifyDeleted(projectId: number): void {
    this.#emit('deleted', projectId);
  }

  #emit(type: ProjectChangeType, projectId: number): void {
    this.#lastChange.set({ type, projectId });
    this.#changeTick.update((tick) => tick + 1);
  }
}
